import {
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Grid,
    Avatar,
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    Fab, Divider, IconButton, MenuItem, Menu, ListItemIcon, Link, useTheme, Tooltip
} from "@mui/material";
import React, {ChangeEvent, forwardRef, useLayoutEffect, useImperativeHandle, useRef, useState} from "react";
import {ChatWorkerClient} from "../chat/ChatWorkerClient";
import type {ChatFrame} from "../chat/connectionTypes";
import {Simulate} from "react-dom/test-utils";
import input = Simulate.input;
import SendIcon from '@mui/icons-material/Send';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
// import ReactTimeAgo from "react-time-ago";
import TimeAgo from 'react-timeago'
import {v4 as uuidv4} from 'uuid';
import {Logout, PersonAdd, Settings, Shuffle, ShuffleOn} from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import apiInstance from "../common/ApiInstance";
import axios from "axios";
import {ToastContainer, toast} from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';

export type ChatProps = {
    disconnect: () => void
}

const chatMessageKey = (message: any): string => message.chatId != null
    ? `chat:${message.chatId}`
    : JSON.stringify([message.type, message.uuid, message.sender, message.date, message.content]);

const mergeChatHistory = (messages: any[]): any[] => {
    const unique = new Map<string, any>();
    messages.forEach(message => {
        if (['CHAT', 'JOIN', 'LEAVE'].includes(message.type)) unique.set(chatMessageKey(message), message);
    });
    let chatCount = 0;
    let noticeCount = 0;
    return Array.from(unique.values()).sort((a, b) => {
        if (a.chatId != null && b.chatId != null) return a.chatId - b.chatId;
        return (Date.parse(a.date) || 0) - (Date.parse(b.date) || 0);
    }).reverse().filter(message => message.type === 'CHAT'
        ? ++chatCount <= 50
        : ++noticeCount <= 50).reverse();
};

// const ChatLogin = () => {
const ChatLogin = forwardRef<ChatProps>((props, ref) => {
    const theme = useTheme();
    const [textFieldValue, setTextFieldValue] = useState<string>('');
    const messageInputRef = useRef<HTMLInputElement>(null);

    let uuid = localStorage.getItem("chatUuid")
    let chatColor = localStorage.getItem("chatColor") || '#a5d6a7'
    const client = useRef<ChatWorkerClient>()
    const clearedPresenceIds = useRef(new Set<number>());
    const [connected, setConnected] = useState(false);
    const [chatUsername, setChatUsername] = useState<string>('');
    const [messages, setMessages] = useState<any[]>([]);
    const [showLogin, setShowLogin] = useState<boolean>(true);
    const [showChat, setShowChat] = useState<boolean>(false);
    const [inputMessage, setInputMessage] = useState<string>('');
    const draftRevision = useRef(0);
    const [sending, setSending] = useState(false);

    // 메뉴
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // file upload
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [filename, setFilename] = useState("");
    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) {
            return;
        }
        const file = e.target.files[e.target.files.length - 1];
        const socket = client.current;
        if (!socket?.connected) {
            toast('연결 복구 후 파일을 다시 선택해주세요.');
            return;
        }
        const {name} = file;
        setFilename(name);

        const formData = new FormData();
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
        formData.append('multipartFiles', file);
        try {
            const it = await axios.post("/api/chat/file", formData, config);
            if (client.current !== socket || !socket.connected) {
                toast('연결이 변경되었습니다. 파일을 다시 선택해주세요.');
                return;
            }
            await socket.publish({
                destination: '/app/chat.sendMessage',
                body: JSON.stringify({
                    uuid: uuid,
                    chatColor: chatColor,
                    sender: chatUsername,
                    content: document.location.origin + '/static/data/' + it.data.filename,
                    type: 'CHAT'
                })
            })
        } catch (error) {
            toast('error:' + error)
        }
    };

    // 상단에 state 추가
    const [videoUrl, setVideoUrl] = useState<string>('');
    const [isVideoOpen, setIsVideoOpen] = useState(false);

    // 비디오 URL 감지를 위한 정규식 수정
    const videoUrlRegex = /\.(mp4|webm|ogg|mov)$/i;

    // 비디오 모달 열기/닫기 핸들러
    const handleVideoOpen = (url: string) => {
        setVideoUrl(url);
        setIsVideoOpen(true);
    };

    const handleVideoClose = () => {
        setVideoUrl('');
        setIsVideoOpen(false);
    };

    React.useEffect(() => {
        try {
            if (uuid === null || chatColor == null) {
                uuid = uuidv4()
                setRandomColor()
                localStorage.setItem("chatUuid", uuid)
            }
        } catch (e) {
            toast('error:' + e)
        }
        const localStorageChatUsername = localStorage.getItem("chatUsername")
        const username = (localStorageChatUsername === '' || localStorageChatUsername === null || localStorageChatUsername === undefined) ? 'USER' : localStorageChatUsername
        setChatUsername(username)
        if (username !== 'USER') {
            // clickLoginButton()
            connect(username)
        }
        return () => {
            const current = client.current;
            client.current = undefined;
            void current?.deactivate();
        };
    }, [])
    React.useEffect(() => {
        localStorage.setItem("chatUsername", chatUsername)
    }, [chatUsername])

    // 부모 컴포넌트에서 사용할 수 있는 함수들을 내부에 표기
    useImperativeHandle(ref, () => ({
        disconnect
    }));

    const setRandomColor = () => {
        const colors = ['#339a40', '#a5d6a7', '#90caf9', '#f48fb1',
            '#ffcc80', '#80cbc4', '#b39ddb', '#c5a762']; // Add more colors as needed
        const randomIndex = Math.floor(Math.random() * colors.length);

        const random = colors[randomIndex]

        localStorage.setItem("chatColor", random)
        chatColor = random

        return random
    };

    const shuffleColor = async () => {
        if (!client.current?.connected) return;
        const random = setRandomColor()
        const convertedMessages = messages.map((item) => {
            if (item.uuid === uuid && item.type !== 'JOIN' && item.type !== 'LEAVE') {
                item.chatColor = random
            }
            return item
        })

        setMessages(convertedMessages)
        try {
            await client.current!.publish({
                destination: '/app/chat.setMessages',
                body: JSON.stringify(convertedMessages)
            });
        } catch {
            toast('연결이 끊겼습니다. 연결 복구 후 다시 시도해주세요.');
        }
    }

    const clickLoginButton = () => {
        setMessages([])
        connect()
    }
    const disconnect = () => {
        const current = client.current;
        client.current = undefined;
        setConnected(false);
        void current?.deactivate();
    }

    const connect = (username: string = chatUsername) => {
        if (client.current?.active) return;
        const wsHost = document.location.host.startsWith('localhost') ? "localhost:8080" : document.location.host
        const wsProtocol = document.location.protocol == 'http:' ? "ws://" : "wss://"
        // 소켓 연결
        try {
            const socket = new ChatWorkerClient({
                brokerURL: wsProtocol + wsHost + "/ws",
                historyDestination: `/topic/history/${uuid}`,
                joinMessage: {uuid, chatColor, sender: username, type: 'JOIN'},
                onWebSocketClose: event => {
                    if (client.current !== socket) return;
                    setConnected(false);
                    if (event?.fatal) toast('채팅 연결을 시작하지 못했습니다. 페이지를 새로고침해주세요.');
                },
                onConnect: () => {
                    if (client.current !== socket) return;
                    // Reload persisted notices, including visits while this client was away.
                    setMessages(chats => chats.filter(message => message.type === 'CHAT'));
                    toast('Connected')
                    subscribe(socket);
                    setConnected(true);
                    setShowLogin(false)
                    setShowChat(true)
                    setTimeout(() => messageInputRef.current?.focus(), 100)
                },
            });
            client.current = socket;
            socket.activate();
        } catch (err) {
            console.log(err);
            toast('err' + err)
        }
    };

    const subscribe = (socket: ChatWorkerClient) => {
        const retainMessage = (message: any) => message.type === 'CHAT'
            || message.chatId == null || !clearedPresenceIds.current.has(Number(message.chatId));
        socket.subscribe('/topic/public', (body: ChatFrame) => {
            if (client.current !== socket) return;
            const json_body = JSON.parse(body.body);
            setMessages(chats => mergeChatHistory([...chats, ...json_body].filter(retainMessage)));
        });
        socket.subscribe(`/topic/history/${uuid}`, (body: ChatFrame) => {
            if (client.current !== socket) return;
            const history = JSON.parse(body.body);
            const clearedIds = body.headers?.['presence-cleared-ids'];
            if (clearedIds) {
                clearedIds.split(',').map(Number).filter(Number.isFinite)
                    .forEach(id => clearedPresenceIds.current.add(id));
            }
            // Include stored presence as well as messages received during history loading.
            setMessages(chats => mergeChatHistory([...history, ...chats].filter(retainMessage)));
        });

    }
    const handleSendMessage = async () => {
        if (inputMessage === '' || sending) {
            return
        }
        if (!client.current?.connected) {
            toast('연결을 복구하고 있습니다. 잠시 후 다시 전송해주세요.');
            return;
        }
        const socket = client.current;
        const outgoing = inputMessage;
        const sentRevision = draftRevision.current;
        setSending(true);
        try {
            await socket.publish({
                destination: '/app/chat.sendMessage',
                body: JSON.stringify({
                    uuid: uuid,
                    chatColor: chatColor,
                    sender: chatUsername,
                    content: outgoing,
                    type: 'CHAT'
                })
            })
        } catch (error) {
            toast('전송하지 못했습니다. 연결 복구 후 다시 전송해주세요.');
            return;
        } finally {
            setSending(false);
        }
        if (client.current !== socket) return;
        if (draftRevision.current === sentRevision) setInputMessage('');
        if (chatUsername != '칸쵸' && chatUsername != '캉쵸' && chatUsername != '조개') {
            const prowlUrl = "https://api.prowlapp.com/publicapi/add?apikey=40c48320c21e8b8e9179a12c0d41e38aa6b613ad&application=SSFIRE&description=SlowQuery"
            apiInstance.get(prowlUrl)
        }
    }
    const scrollRef = useRef<HTMLDivElement>(null);

    // URL을 감지하기 위한 정규식 추가
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const handleClickLink = ((e: React.MouseEvent<HTMLElement>) => {
        // viewFile("www.naver.com")
        // const host = document.location.host.startsWith('localhost') ? "localhost:8080" : "ddanzit.donnert.net"
        const url = e.currentTarget.textContent!
        window.open(url, '_blank')
    })
    const chatBoxRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        // Reveal received messages in the same paint, without a debounce delay.
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [messages, showChat]);

    const changeUsername = () => {
        disconnect()
        setShowLogin(true)
        setShowChat(false)
    }
    const timeForToday = (value: any) => {
        const today = new Date();
        const timeValue = new Date(value);

        const betweenTime = Math.floor(
            (today.getTime() - timeValue.getTime()) / 1000 / 60
        );
        if (betweenTime < 1) return "방금전";
        if (betweenTime < 180) {
            return `${betweenTime}분전`;
        }

        const betweenTimeHour = Math.floor(betweenTime / 60);
        if (betweenTimeHour < 24) {
            return `${betweenTimeHour}시간전`;
        }

        const betweenTimeDay = Math.floor(betweenTime / 60 / 24);
        if (betweenTimeDay < 365) {
            return `${betweenTimeDay}일전`;
        }

        return `${Math.floor(betweenTimeDay / 365)}년전`;
    };
    return (
        <>
            {showLogin &&
                <Container component="main" maxWidth="xs">
                    <Paper elevation={3} style={{padding: '20px', marginTop: '20px'}}>
                        <Typography variant="h5">딴짓</Typography>
                        <Box>
                            <TextField
                                label="Nickname"
                                variant="outlined"
                                margin="normal"
                                value={chatUsername}
                                fullWidth
                                onChange={(e) => setChatUsername(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation()
                                    if (e.key === 'Enter') clickLoginButton();
                                }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={clickLoginButton}
                            >
                                Login
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            }

            {showChat &&
                <Box
                    style={{
                        height: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/*<TextField size="small" rows={2} fullWidth={true} multiline={true}*/}
                    {/*           value={JSON.stringify(messages)}/>*/}
                    <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload}/>
                    {!connected && <Typography role="status" color="warning.main">
                        연결이 끊어져 자동으로 재연결하고 있습니다. 작성 중인 메시지는 유지됩니다.
                    </Typography>}
                    <Paper
                        ref={chatBoxRef}  // ← 추가
                        elevation={1} style={{
                        flex: 1,
                        padding: '5px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {messages.map((message) => (
                            <Box key={chatMessageKey(message)} sx={{
                                backgroundColor: (message?.uuid === uuid)
                                    ? 'action.selected'  // 다크/라이트 모드 자동 대응
                                    : 'transparent'
                            }}>
                                <div style={{
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                }}>
                                    {
                                        (message?.type === 'JOIN') ? (
                                            <div style={{textAlign: 'center'}}>
                                                <Tooltip title={timeForToday(message.date)} placement={'left'}
                                                         sx={{width: '100%'}}>
                                                    <Typography variant="caption" display={"inline"}
                                                                sx={{fontWeight: 'bold'}}
                                                                style={{
                                                                    textAlign: "center",
                                                                    width: "100%"
                                                                }}>
                                                        {message.sender}님이 들어왔습니다
                                                    </Typography>
                                                </Tooltip>
                                            </div>
                                        ) : (message?.type === 'LEAVE') ? (
                                            <div style={{textAlign: 'center'}}>
                                                <Tooltip title={timeForToday(message.date)} placement={'left'}>
                                                    <Typography variant="caption" display={"inline"}
                                                                sx={{fontWeight: 'light'}}>
                                                        {message.sender}님이 나갔습니다
                                                    </Typography>
                                                </Tooltip>
                                            </div>
                                        ) : (message?.type === 'CHAT') ? (
                                            <React.Fragment>
                                                <Tooltip title={timeForToday(message.date)} placement={'left'}>
                                                    <Typography variant={"body2"} gutterBottom display={"inline"}
                                                                sx={{fontWeight: 'bold'}}>
                                                        {message.sender}:
                                                    </Typography>
                                                </Tooltip>
                                                <Typography display="inline" variant="body2" gutterBottom>
                                                    {message.content.split(urlRegex).map((part: string, i: number) => {
                                                        if (part.match(urlRegex)) {
                                                            if (part.match(videoUrlRegex)) {
                                                                return (
                                                                    <Link
                                                                        key={i}
                                                                        style={{cursor: 'pointer'}}
                                                                        onClick={() => handleVideoOpen(part)}>
                                                                        {part}
                                                                    </Link>
                                                                );
                                                            }
                                                            return (
                                                                <Link
                                                                    key={i}
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={() => window.open(part, '_blank')}>
                                                                    {part}
                                                                </Link>
                                                            );
                                                        }
                                                        return part;
                                                    })}
                                                </Typography>
                                            </React.Fragment>
                                        ) : <div/>
                                    }
                                </div>
                            </Box>
                        ))}
                        <div ref={scrollRef}></div>
                    </Paper>
                    <Box style={{marginTop: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center'}}>

                        <TextField
                            inputRef={messageInputRef}
                            variant="outlined"
                            fullWidth
                            rows={1}
                            value={inputMessage}
                            onChange={(e) => {
                                draftRevision.current++;
                                setInputMessage(e.target.value);
                            }}
                            style={{marginRight: '10px', flex: 1, height: '40px'}} // 높이 조절을 위한 스타일 추가
                            InputProps={{style: {height: '100%'}}} // InputProps에 높이 조절을 위한 스타일 추가
                            onKeyUp={(ev) => {
                                if (ev.key === 'Enter' && !ev.nativeEvent.isComposing) {
                                    ev.preventDefault();
                                    handleSendMessage()
                                }
                            }}
                        />
                        <IconButton aria-label="send" disabled={!connected || sending} onClick={handleSendMessage}>
                            <SendIcon/>
                        </IconButton>
                        <IconButton aria-label={"파일업로드"} disabled={!connected} onClick={() => fileInputRef.current!.click()}>
                            <UploadFileIcon/>
                        </IconButton>
                        <IconButton aria-label={"이름변경"} onClick={() => {
                            handleMenuClose()
                            changeUsername()
                        }}>
                            <DriveFileRenameOutlineIcon/>
                        </IconButton>
                        {/*<Grid item xs={1} style={{alignItems: "right"}}>*/}
                        {/*    <IconButton*/}
                        {/*        aria-label="more"*/}
                        {/*        id="long-button"*/}
                        {/*        aria-controls={menuOpen ? 'long-menu' : undefined}*/}
                        {/*        aria-expanded={menuOpen ? 'true' : undefined}*/}
                        {/*        aria-haspopup="true"*/}
                        {/*        onClick={handleMenuClick}*/}
                        {/*    >*/}
                        {/*        <MoreVertIcon/>*/}
                        {/*    </IconButton>*/}
                        {/*    <Menu*/}
                        {/*        anchorEl={anchorEl}*/}
                        {/*        id="account-menu"*/}
                        {/*        open={menuOpen}*/}
                        {/*        onClose={handleMenuClose}*/}
                        {/*        onClick={handleMenuClose}*/}
                        {/*    >*/}
                        {/*        <MenuItem onClick={() => {*/}
                        {/*            handleMenuClose()*/}
                        {/*            changeUsername()*/}
                        {/*        }}>*/}
                        {/*            <ListItemIcon>*/}
                        {/*                <DriveFileRenameOutlineIcon fontSize="small"/>*/}
                        {/*            </ListItemIcon>*/}
                        {/*            이름변경*/}
                        {/*        </MenuItem>*/}
                        {/*    </Menu>*/}
                        {/*</Grid>*/}
                    </Box>
                </Box>
            }
            {isVideoOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%'
                    }}>
                        <IconButton
                            onClick={handleVideoClose}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                color: 'white',
                                zIndex: 10000
                            }}
                        >
                            <CloseIcon/>
                        </IconButton>
                        <video
                            controls
                            autoPlay
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        >
                            <source src={videoUrl} type="video/mp4"/>
                            <source src={videoUrl} type="video/quicktime"/>
                            <source src={videoUrl} type="video/webm"/>
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            )}
        </>
    )
})
export default ChatLogin;
